// ============================================================
// CommandCenter/shell/icons.tsx — the Square Code V2 icon system.
//
// PHASE_2M hard rule: NO emoji anywhere in the V2 UI. Every pictograph is a
// real inline SVG component (adapted from the operator's Quam notification
// design language — notifications-live.jsx), animated via SVG <animate> or
// CSS where it helps. All icons share one API: { color, size, style }.
//
// Keep icons tiny and stroke-based so they inherit the V2 ink palette.
// ============================================================

import type { CSSProperties } from 'react'

export interface IconProps {
  color?: string
  size?: number
  style?: CSSProperties
  /** Turn off the built-in SVG animation (e.g. for dense lists). */
  still?: boolean
}

const base = (size: number, style?: CSSProperties) => ({
  width: size, height: size, flexShrink: 0 as const, display: 'inline-block', verticalAlign: '-0.18em', ...style,
})

// ---- channels (from the Quam notifications design) -------------------------

export function EmailIcon({ color = 'currentColor', size = 16, style, still }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="3" fill={color} opacity="0.16" stroke={color} strokeWidth="1.5" />
      <path d="M2 7l10 6 10-6" stroke={color} strokeWidth="1.8" fill="none" opacity="0.85">
        {!still && <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />}
      </path>
    </svg>
  )
}

export function PhoneIcon({ color = 'currentColor', size = 16, style, still }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 5a2 2 0 012-2z" stroke={color} strokeWidth="1.5" fill={color} opacity="0.18" />
      <path d="M16 2c2 0.5 3.5 2 4 4" stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.7">
        {!still && <animate attributeName="opacity" values="0.3;0.9;0.3" dur="1s" repeatCount="indefinite" />}
      </path>
    </svg>
  )
}

export function MessageIcon({ color = 'currentColor', size = 16, style, still }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M21 12a9 9 0 01-9 9 9 9 0 01-6.7-3L2 21l2.7-3.3A9 9 0 1121 12z" stroke={color} strokeWidth="1.5" fill={color} opacity="0.16" />
      {[8, 12, 16].map((cx, i) => (
        <circle key={cx} cx={cx} cy="12" r="1.2" fill={color}>
          {!still && <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" begin={`${i * 0.2}s`} repeatCount="indefinite" />}
        </circle>
      ))}
    </svg>
  )
}

export function CalendarIcon({ color = 'currentColor', size = 16, style, still }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="3" stroke={color} strokeWidth="1.5" fill={color} opacity="0.16" />
      <line x1="3" y1="10" x2="21" y2="10" stroke={color} strokeWidth="1" opacity="0.35" />
      <line x1="8" y1="2" x2="8" y2="6" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
      <line x1="16" y1="2" x2="16" y2="6" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
      <circle cx="12" cy="15" r="2" fill={color} opacity="0.8">
        {!still && <animate attributeName="r" values="2;2.8;2" dur="1.8s" repeatCount="indefinite" />}
      </circle>
    </svg>
  )
}

export function BellIcon({ color = 'currentColor', size = 16, style, still }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3a6 6 0 00-6 6v3.5L4 16h16l-2-3.5V9a6 6 0 00-6-6z" stroke={color} strokeWidth="1.5" fill={color} opacity="0.16" />
      <path d="M10 19a2 2 0 004 0" stroke={color} strokeWidth="1.5" strokeLinecap="round">
        {!still && <animate attributeName="opacity" values="0.4;1;0.4" dur="1.6s" repeatCount="indefinite" />}
      </path>
    </svg>
  )
}

// ---- meetings / video (animated — the PHASE_2M meeting icon) ---------------

export function MeetingIcon({ color = 'currentColor', size = 16, style, still }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2" y="6" width="13" height="12" rx="3" stroke={color} strokeWidth="1.5" fill={color} opacity="0.16" />
      <path d="M15 10.5l6-3.5v10l-6-3.5" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill={color} opacity="0.5" />
      <circle cx="8.5" cy="12" r="2" fill={color} opacity="0.8">
        {!still && <animate attributeName="opacity" values="0.45;1;0.45" dur="1.8s" repeatCount="indefinite" />}
      </circle>
    </svg>
  )
}

// ---- files / links / notes (calendar event workspace) -----------------------

export function FileIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 2h8l5 5v15H6z" stroke={color} strokeWidth="1.5" fill={color} opacity="0.16" strokeLinejoin="round" />
      <path d="M14 2v5h5" stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round" />
    </svg>
  )
}

export function LinkIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M10 14a5 5 0 007.07 0l2.83-2.83a5 5 0 00-7.07-7.07L11 5.9" stroke={color} strokeWidth="1.7" strokeLinecap="round" fill="none" />
      <path d="M14 10a5 5 0 00-7.07 0L4.1 12.83a5 5 0 007.07 7.07L13 18.1" stroke={color} strokeWidth="1.7" strokeLinecap="round" fill="none" />
    </svg>
  )
}

export function NoteIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 4h16v12l-4 4H4z" stroke={color} strokeWidth="1.5" fill={color} opacity="0.16" strokeLinejoin="round" />
      <path d="M16 20v-4h4" stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round" />
      <line x1="8" y1="9" x2="16" y2="9" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
      <line x1="8" y1="13" x2="13" y2="13" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
    </svg>
  )
}

// ---- actions (context menu / buttons) ---------------------------------------

export function OpenIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  // external/deep-open arrow — replaces the old up-right pictograph
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 17L17 7M9 7h8v8" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  )
}

export function ShareIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="6" cy="12" r="2.4" stroke={color} strokeWidth="1.5" fill={color} opacity="0.7" />
      <circle cx="17" cy="5.5" r="2.4" stroke={color} strokeWidth="1.5" fill={color} opacity="0.7" />
      <circle cx="17" cy="18.5" r="2.4" stroke={color} strokeWidth="1.5" fill={color} opacity="0.7" />
      <path d="M8.2 10.8l6.6-4M8.2 13.2l6.6 4" stroke={color} strokeWidth="1.5" fill="none" />
    </svg>
  )
}

export function DownloadIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3v11m0 0l-4-4m4 4l4-4" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M4 17v3h16v-3" stroke={color} strokeWidth="1.8" strokeLinecap="round" fill="none" />
    </svg>
  )
}

export function TrashIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 7h14M9 7V4h6v3M7 7l1 13h8l1-13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <line x1="10.2" y1="11" x2="10.2" y2="16.5" stroke={color} strokeWidth="1.3" strokeLinecap="round" opacity="0.7" />
      <line x1="13.8" y1="11" x2="13.8" y2="16.5" stroke={color} strokeWidth="1.3" strokeLinecap="round" opacity="0.7" />
    </svg>
  )
}

export function CopyIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="8" y="8" width="12" height="12" rx="2" stroke={color} strokeWidth="1.5" fill={color} opacity="0.16" />
      <path d="M5 15H4a1 1 0 01-1-1V4a1 1 0 011-1h10a1 1 0 011 1v1" stroke={color} strokeWidth="1.5" fill="none" />
    </svg>
  )
}

export function ScheduleIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.5" fill={color} opacity="0.14" />
      <path d="M12 7.5V12l3 2" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  )
}

export function PublishIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M4 14v5a1 1 0 001 1h14a1 1 0 001-1v-5" stroke={color} strokeWidth="1.6" strokeLinecap="round" fill="none" />
    </svg>
  )
}

export function CheckIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4.5 12.5l5 5L19.5 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  )
}

export function WarnIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3L2.5 20h19L12 3z" stroke={color} strokeWidth="1.5" fill={color} opacity="0.16" strokeLinejoin="round" />
      <line x1="12" y1="9.5" x2="12" y2="14.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="17.2" r="1.1" fill={color} />
    </svg>
  )
}

export function PlusIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  )
}

// ---- structure (projects / divisions / portals) -----------------------------

export function ProjectIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" stroke={color} strokeWidth="1.5" fill={color} opacity="0.16" />
    </svg>
  )
}

export function PortalIcon({ color = 'currentColor', size = 16, style, still }: IconProps) {
  // concentric portal rings — the portal-opening motif
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <ellipse cx="12" cy="12" rx="8.5" ry="9.5" stroke={color} strokeWidth="1.4" fill="none" opacity="0.85" />
      <ellipse cx="12" cy="12" rx="5" ry="6.2" stroke={color} strokeWidth="1.2" fill={color} opacity="0.22" />
      <circle cx="12" cy="12" r="1.6" fill={color}>
        {!still && <animate attributeName="r" values="1.6;2.4;1.6" dur="2.2s" repeatCount="indefinite" />}
      </circle>
    </svg>
  )
}

export function ShieldIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2l8 3.5V11c0 5-3.2 8.9-8 11-4.8-2.1-8-6-8-11V5.5L12 2z" stroke={color} strokeWidth="1.5" fill={color} opacity="0.16" strokeLinejoin="round" />
      <path d="M8.8 12l2.2 2.2 4.2-4.4" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  )
}

export function TaskIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" stroke={color} strokeWidth="1.5" fill={color} opacity="0.14" />
      <path d="M8 12.2l2.6 2.6L16.5 9" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  )
}

export function UsersIcon({ color = 'currentColor', size = 16, style }: IconProps) {
  return (
    <svg style={base(size, style)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3.4" stroke={color} strokeWidth="1.5" fill={color} opacity="0.18" />
      <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" />
      <circle cx="17" cy="9" r="2.6" stroke={color} strokeWidth="1.3" fill={color} opacity="0.3" />
      <path d="M16.5 14.5c2.7.3 4.5 2.2 4.5 4.8" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none" opacity="0.7" />
    </svg>
  )
}

export function ChevronIcon({ color = 'currentColor', size = 16, style, direction = 'right' }: IconProps & { direction?: 'right' | 'down' | 'left' | 'up' }) {
  const rot = { right: 0, down: 90, left: 180, up: 270 }[direction]
  return (
    <svg style={{ ...base(size, style), transform: `rotate(${rot}deg)`, transition: 'transform .2s' }} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 5l7 7-7 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  )
}
